import { canView, type AdminMenuCounts as ServerMenuCounts } from '@nest-recruit/shared';
import { useCurrentSession } from '../../stores/session';
import type { AdminSectionId } from './admin-sections';
import { useServerMenuCounts } from './api/overview-api';

/** A red to-do count per menu item (FRAME-SPEC §2): only things somebody has to do, never plain totals. */
export type AdminMenuCounts = Readonly<Partial<Record<AdminSectionId, number>>>;

const NO_COUNTS: AdminMenuCounts = {};

/**
 * The server's counts, placed on their menu items. Null means this person may not open that section, so it is
 * left out rather than drawn as 0.
 */
export function menuCountsFrom(counts: ServerMenuCounts | undefined): AdminMenuCounts {
  if (!counts) return NO_COUNTS;
  return {
    ...(counts.pendingInvites !== null ? { people: counts.pendingInvites } : {}),
    ...(counts.failedEmailsToday !== null ? { email: counts.failedEmailsToday } : {}),
    // Admin redesign batch 2: Background work's failed jobs in the last 24 hours (null too while Redis is away).
    ...(counts.failedJobsLast24h !== null ? { 'background-work': counts.failedJobsLast24h } : {}),
    // Admin redesign batch 2: Inbound mail's emails waiting to be filed (null too while Inbound email is off).
    ...(counts.inboxWaiting !== null ? { inbox: counts.inboxWaiting } : {}),
  };
}

/**
 * The counts the Admin menu shows (brief 2026-11-02 §4): People's invitations not accepted after 3 days (expired
 * links included, the Overview's own rule) and Email delivery's failures today. Counted live by the server, never
 * stored (non-negotiable #10).
 *
 * Only the Admin frame calls this, so the request runs only inside Admin, and only for someone who may open People
 * or Email delivery and Background work (all three need Admin settings or People).
 */
export function useAdminMenuCounts(): AdminMenuCounts {
  const session = useCurrentSession();
  const mayCount = session !== null && (canView(session, 'user_management') || canView(session, 'admin_config'));
  const { data } = useServerMenuCounts(mayCount);
  return mayCount ? menuCountsFrom(data) : NO_COUNTS;
}
